import React, { useState, useRef, useEffect } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Clock } from 'lucide-react';
import { cn } from '@/lib/utils';

const TimePicker = ({ value, onChange, id, name }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hour, setHour] = useState('12');
  const [minute, setMinute] = useState('00');
  const [period, setPeriod] = useState('PM');

  useEffect(() => {
    if (value) {
      const [h, m] = value.split(':');
      const h24 = parseInt(h, 10);
      if (h24 === 0) {
        setHour('12');
        setPeriod('AM');
      } else if (h24 === 12) {
        setHour('12');
        setPeriod('PM');
      } else if (h24 > 12) {
        setHour(String(h24 - 12).padStart(2, '0'));
        setPeriod('PM');
      } else {
        setHour(String(h24).padStart(2, '0'));
        setPeriod('AM');
      }
      setMinute(m);
    }
  }, [value]);

  const handleTimeChange = (newHour, newMinute, newPeriod) => {
    let h24 = parseInt(newHour, 10);
    if (newPeriod === 'PM' && h24 < 12) {
      h24 += 12;
    }
    if (newPeriod === 'AM' && h24 === 12) {
      h24 = 0;
    }
    const formattedTime = `${String(h24).padStart(2, '0')}:${newMinute}`;
    onChange(formattedTime);
  };

  const handleHourClick = (h) => {
    const newHour = String(h).padStart(2, '0');
    setHour(newHour);
    handleTimeChange(newHour, minute, period);
  };

  const handleMinuteClick = (m) => {
    const newMinute = String(m).padStart(2, '0');
    setMinute(newMinute);
    handleTimeChange(hour, newMinute, period);
  };

  const handlePeriodClick = (p) => {
    setPeriod(p);
    handleTimeChange(hour, minute, p);
  };

  const TimeColumn = ({ values, onSelect, selectedValue, title }) => (
    <div className="flex flex-col items-center space-y-1 h-48 overflow-y-auto px-2">
      <p className="text-xs text-gray-500 sticky top-0 !bg-white py-1">{title}</p>
      {values.map((v) => (
        <button
          key={v}
          type="button"
          onClick={() => onSelect(v)}
          className={cn(
            'w-12 py-1 rounded-md text-center transition-colors text-gray-900',
            selectedValue === String(v).padStart(2, '0') || selectedValue === v
              ? 'bg-brand-gold text-brand-dark font-bold'
              : 'hover:bg-brand-gold/20'
          )}
        >
          {String(v).padStart(2, '0')}
        </button>
      ))}
    </div>
  );

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button id={id} name={name} variant="outline" className="w-full justify-start text-left font-normal h-12 text-lg !bg-white !text-gray-900 !border-gray-300 hover:!bg-gray-50 hover:!text-gray-900">
          <Clock className="mr-2 h-4 w-4" />
          {value ? `${hour}:${minute} ${period}` : <span>Pick a time</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 !bg-white !text-gray-900 !border-gray-300">
        <div className="flex p-2">
          <TimeColumn values={Array.from({ length: 12 }, (_, i) => i + 1)} onSelect={handleHourClick} selectedValue={hour} title="Hour" />
          <TimeColumn values={Array.from({ length: 12 }, (_, i) => i * 5)} onSelect={handleMinuteClick} selectedValue={minute} title="Minute" />
          <div className="flex flex-col items-center space-y-1 px-2">
            <p className="text-xs text-gray-500 sticky top-0 !bg-white py-1">Period</p>
            <button type="button" onClick={() => handlePeriodClick('AM')} className={cn('w-12 py-1 rounded-md text-gray-900', period === 'AM' ? 'bg-brand-gold text-brand-dark font-bold' : 'hover:bg-brand-gold/20')}>AM</button>
            <button type="button" onClick={() => handlePeriodClick('PM')} className={cn('w-12 py-1 rounded-md text-gray-900', period === 'PM' ? 'bg-brand-gold text-brand-dark font-bold' : 'hover:bg-brand-gold/20')}>PM</button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export { TimePicker };